import i18next from 'i18next';
import _ from 'lodash';
import moment from 'moment-timezone';
import tinycolor from 'tinycolor2';
import { sqWorkbenchStore } from '@/core/core.stores';
import { isDurationValid, isTimestampNanos, nanosToMillis } from '@/datetime/dateTime.utilities';
import { getPreferredTimeFormat } from '@/datetime/dateTimeFormatting.utilities';
import {
  AUTO_SIZE_MENU_ACTIONS,
  CONDITION_EXTRA_COLUMNS,
  DEFAULT_BACKGROUND_DARK,
  DEFAULT_BACKGROUND_LIGHT,
  DEFAULT_TEXT_COLOR_DARK,
  NULL_PLACEHOLDER,
  SQ_CELL_BACKGROUND_COLOR_VAR,
  STRIPED_CELL_COLOR,
  STRIPED_CELL_COLOR_DARK,
  STRIPED_PRIORITY_BACKGROUND_MIX_AMOUNT,
  TableBuilderColumnType,
  TableBuilderHeaderType,
  TextHeaderMenuAction,
} from '@/tableBuilder/tableBuilder.constants';
import { ANALYSIS_COLORS } from '@/trend/trendViewer/trendViewer.constants';
import { COLUMNS_AND_STATS } from '@/trendData/trendData.constants';
import { formatNumber } from '@/utilities/numberHelper.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { isStringSeries } from '@/utilities/utilities';
import { SeeqNames } from '@seeqdev/constants';
import type {
  ColumnOrRow,
  ColumnOrRowWithDefinitions,
  ConditionTableHeader,
  GroupingAndSortingValue,
  SimpleTableCell,
  TableBuilderHeaders,
  TableBuilderRowData,
} from '@/tableBuilder/tableBuilder.types';
import type { RangeExport } from '@/trendData/duration.types';
import type { ITEM_TYPES } from '@/trendData/trendData.constants';
import type { Signal } from '@/utilities/items.types';
import type { FormatOptions } from '@/utilities/numberHelper.utilities';
import type { AgGridReact, CellStyle, ColDef } from '@seeqdev/ag/grid';

const isReadableColorCache = new Map();

/**
 * Formats a point in time for display in a table using the table's Date Format setting, the user's 12/24 hour
 * preference, and the table's timezone. This is the formatting shared by the header cells and by any cell that holds
 * a point in time.
 *
 * @param millis - The time in milliseconds since the epoch
 * @param format - The table's Date Format setting
 * @param timezone - The timezone to display the date in
 * @returns The formatted date
 */
export function formatTableDate(millis: number, format: string, timezone: { name: string }): string {
  const effectiveFormat = getPreferredTimeFormat(sqWorkbenchStore.use24HourFormat, format);
  return moment.utc(millis).tz(timezone.name).format(effectiveFormat);
}

/**
 * Formats the text for a column header.
 *
 * @param headerInfo - Header formatting information
 * @param headerInfo.type - One of TableBuilderHeaderType enumeration
 * @param headerInfo.format - Format string to use if .type is a format involving date/times
 * @param property - Custom property
 * @param startTime - Start time for the cell
 * @param endTime - End time for the cell
 * @param timezone - timezone to run the table for
 * @param defaultHeader - Default header to use if no other header is provided
 * @returns The formatted header
 */
export function formatHeader(
  headerInfo: { type: TableBuilderHeaderType; format: string },
  property: string | undefined,
  startTime: number,
  endTime: number,
  timezone: { name: string },
  defaultHeader = '',
): string {
  const formatDate = (date: number, isStart: boolean) =>
    _.isNil(date)
      ? i18next.t(`TABLE_BUILDER.${isStart ? 'STARTS_OUT_OF_RANGE' : 'ENDS_OUT_OF_RANGE'}`)
      : formatTableDate(date, headerInfo.format, timezone);

  if (headerInfo.type === TableBuilderHeaderType.None) {
    return defaultHeader;
  } else if (headerInfo.type === TableBuilderHeaderType.CapsuleProperty) {
    return property ?? defaultHeader;
  } else if (headerInfo.type === TableBuilderHeaderType.Start) {
    return formatDate(startTime, true);
  } else if (headerInfo.type === TableBuilderHeaderType.End) {
    return formatDate(endTime, false);
  } else {
    return `${formatDate(startTime, true)} - ${formatDate(endTime, false)}`;
  }
}

/**
 * Formats a metric value for display in the scorecard table.
 *
 * @param value - The value of the metric
 * @param formatOptions - the format options to be used when formatting the cell
 * @param [column] - One of the columns from COLUMNS
 * @returns The formatted value
 */
export function formatMetricValue(
  value: unknown,
  formatOptions: FormatOptions,
  column: { key?: string; style?: string; format?: string } = {},
): string {
  if (_.isNil(value)) {
    return column.style === 'string' ? '' : NULL_PLACEHOLDER;
  } else if (_.isNumber(value)) {
    return formatNumber(
      value,
      {
        ...formatOptions,
        format: column.format || formatOptions?.format,
      },
      _.noop,
    );
  } else {
    // A regex which replaces <br>, <br />, and either of the previous with extra whitespace with a newline
    return String(value).replace(/<br\s*\/?>/g, '\n');
  }
}

/**
 * Computes the style for cell.
 * @param args - Input values.
 * @param args.backgroundColor - Background color (example use: the user styled the whole column a particular color)
 * @param args.textColor - Text color
 * @param args.textStyle - An array with text styles
 * @param args.textAlign - Text align
 * @param args.priorityBackgroundColor - A color that is prioritized over the background color (example use: metric
 * threshold red, yellow, blue, or neutral [no color], the "unstriped" color of a striped table)
 * @param args.fallbackBackgroundColor - Fallback color mixed with priority backgrounds on striped even rows
 * @param args.darkMode - True if application is in dark mode
 * @param args.whiteSpace - The white space style to use
 * @returns An object containing HTML styles attributes and values
 */
export interface ComputeCellStyleArgs {
  backgroundColor?: string;
  textColor?: string;
  textStyle?: string[];
  textAlign?: string;
  priorityBackgroundColor?: string;
  fallbackBackgroundColor?: string;
  darkMode?: boolean;
  whiteSpace?: string;
}

export function computeCellStyle({
  backgroundColor,
  textColor,
  textStyle = [],
  textAlign = 'left',
  priorityBackgroundColor,
  fallbackBackgroundColor,
  whiteSpace,
}: ComputeCellStyleArgs = {}): CellStyle {
  let bgColor: string = SeeqNames.Properties.Unset;
  if (priorityBackgroundColor && priorityBackgroundColor !== SeeqNames.Properties.Unset) {
    bgColor = priorityBackgroundColor;
    if (fallbackBackgroundColor) {
      bgColor = tinycolor
        .mix(priorityBackgroundColor, fallbackBackgroundColor, STRIPED_PRIORITY_BACKGROUND_MIX_AMOUNT)
        .toHexString();
    }
  } else if (backgroundColor && !priorityBackgroundColor) {
    bgColor = backgroundColor;
  } else if (fallbackBackgroundColor) {
    bgColor = fallbackBackgroundColor;
  }

  let textColorToUse;
  if (textColor) {
    textColorToUse = textColor;
  } else if (bgColor !== SeeqNames.Properties.Unset) {
    textColorToUse = tinycolor(bgColor).isDark() ? '#fff' : '#000';
  } else {
    textColorToUse = 'inherit';
  }

  const style: CellStyle = {
    color: textColorToUse,
    fontWeight: _.includes(textStyle, 'bold') ? 'bold' : 'normal',
    fontStyle: _.includes(textStyle, 'italic') ? 'italic' : 'normal',
    textDecoration: _.filter(textStyle, (textDecoration) =>
      _.includes(['overline', 'line-through', 'underline'], textDecoration),
    ).join(' '),
    textAlign,
    // Always set background styles so AG Grid clears stale inline values when colors are removed.
    backgroundColor: bgColor,
    [SQ_CELL_BACKGROUND_COLOR_VAR]: bgColor,
  };

  if (whiteSpace) {
    style.whiteSpace = whiteSpace;
  }
  return style;
}

/**
 * Cached version of {@code tinycolor.isReadable}. The original function is not particularly slow but when calling
 * it many times in tables with thousands of cells the cache improves performance.
 * @param backgroundColor - The background color.
 * @param textColor - The text color.
 * @return True if the text is readable on the specified background
 */
function isReadableColor(backgroundColor: string, textColor: string): boolean {
  const cacheKey = backgroundColor + textColor;
  let isReadable = isReadableColorCache.get(cacheKey);
  if (_.isUndefined(isReadable)) {
    isReadable = tinycolor.isReadable(backgroundColor, textColor);
    isReadableColorCache.set(cacheKey, isReadable);
  }
  return isReadable;
}

/**
 * Computes a foreground that contrasts with that color so that it is readable.
 *
 * @param backgroundColor - The background color. Default to white if not specified.
 * @param darkMode - true if dark mode is used to render the application
 * @return Styles for the background and foreground colors
 */
export function computeCellColors(
  backgroundColor: string | undefined,
  darkMode: boolean | undefined,
): {
  backgroundColor: string;
  color: string;
} {
  const defaultBackgroundColor = darkMode ? DEFAULT_BACKGROUND_DARK : DEFAULT_BACKGROUND_LIGHT;
  const color = backgroundColor ?? defaultBackgroundColor;
  const defaultTextColor = darkMode ? DEFAULT_TEXT_COLOR_DARK : '#000';
  return {
    backgroundColor: color,
    color: isReadableColor(color, defaultTextColor) ? defaultTextColor : '#fff',
  };
}

/**
 * Gets the striped color based on the current state of isTableStriped and the row index passed in
 */
export function getStripedColor(isStriped: boolean, rowIndex: number, darkMode: boolean): string | undefined {
  const stripedCellColor = darkMode ? STRIPED_CELL_COLOR_DARK : STRIPED_CELL_COLOR;
  return isStriped && rowIndex % 2 === 0 ? stripedCellColor : undefined;
}

/**
 * Returns the striped fallback background for priority/metric cells on even rows.
 */
export function getStripedPriorityFallbackBackground(
  isStriped: boolean,
  rowIndex: number | null | undefined,
  darkMode: boolean,
  priorityBackgroundColor?: string,
): string | undefined {
  if (!priorityBackgroundColor || priorityBackgroundColor === SeeqNames.Properties.Unset || !_.isNumber(rowIndex)) {
    return undefined;
  }

  return getStripedColor(isStriped, rowIndex, darkMode);
}

export function hasNumericAndStringItems(items: Array<{ itemType: string } & Partial<Signal>>, itemType: ITEM_TYPES) {
  return _.chain(items)
    .filter((item) => item.itemType === itemType)
    .partition((signal) => isStringSeries(signal))
    .every((group) => !_.isEmpty(group))
    .value();
}

export function getMostReadableIconType(background = '#fff') {
  const mostReadable = tinycolor.mostReadable(background, [ANALYSIS_COLORS.DARK_PRIMARY, ANALYSIS_COLORS.LIGHT_COLOR]);
  return mostReadable.toString() === ANALYSIS_COLORS.DARK_PRIMARY ? 'theme' : 'theme-light';
}

const tryCopyToClipboard = (tableRef: Node | AgGridReact, copy: () => void) => {
  try {
    if (!tableRef) {
      errorToast({ messageKey: 'TABLE_BUILDER.COPY_TO_CLIPBOARD_EMPTY' });
    } else copy();
    successToast({ messageKey: 'TABLE_BUILDER.COPY_TO_CLIPBOARD_SUCCESS' });
  } catch (err) {
    errorToast({ messageKey: 'TABLE_BUILDER.COPY_TO_CLIPBOARD_ERROR' });
  }
};

export function copyAgGridTableToClipboard(agGridRef: AgGridReact, includeHeaders = true) {
  tryCopyToClipboard(agGridRef, () => {
    const gridApi = agGridRef.api;
    gridApi.selectAll();
    gridApi.copySelectedRowsToClipboard({ includeHeaders });
  });
}

export function isPropertyOrStatColumn(column: ColumnOrRow): boolean {
  return isPropertyColumnType(column) || !!column.signalId || _.includes(CONDITION_EXTRA_COLUMNS, column.key);
}

export function isPropertyOrStatOrMetricColumn(column: ColumnOrRow): boolean {
  return isPropertyOrStatColumn(column) || !!column.metricId;
}

export function isPropertyColumnType(column: ColumnOrRow): boolean {
  return _.includes([TableBuilderColumnType.Property, TableBuilderColumnType.CapsuleProperty], column.type);
}

export function findParent(node: HTMLElement, type: string, className?: string): HTMLElement {
  let parentNode = node?.parentElement;
  while (
    parentNode?.parentElement &&
    !(parentNode.nodeName === type && (!className || parentNode.className.split(' ').includes(className)))
  ) {
    parentNode = parentNode.parentElement;
  }
  return parentNode as HTMLElement;
}

/**
 * Finds the size of the earliest parent node of the given node with the given type
 * @param node
 * @param type
 * @param className
 * @returns The height and width of the parent node, or undefined
 */
export function findParentSize(
  node: HTMLElement,
  type: string,
  className?: string,
): { height: number; width: number } | undefined {
  const parentNode = findParent(node, type, className);
  return parentNode ? { height: parentNode.clientHeight, width: parentNode.clientWidth } : undefined;
}

/**
 * Utility method that determines if a given table column is a text column
 * @param column table column
 * @returns True if the given column is a text column, false otherwise
 */
export const isTextColumn = (column: ColumnOrRowWithDefinitions) =>
  _.isUndefined(column.style) ? false : ['assets', 'string', 'fullpath'].includes(column.style);

/**
 * Determines whether a capsule property column holds timestamps, so that it should be displayed as dates rather than
 * as bare numbers. Empty values are ignored; every present value must look like a timestamp and there must be at
 * least one.
 *
 * @param rawValues - The raw values of the column, one per capsule
 * @returns True if the column's values are all nanosecond timestamps
 */
export const areTimestampPropertyValues = (rawValues: unknown[]): boolean => {
  const presentValues = rawValues.filter((value) => value !== null && value !== undefined);
  return presentValues.length > 0 && presentValues.every(isTimestampNanos);
};

/**
 * Formats a timestamp capsule property value the same way the start and end columns are formatted, so that a property
 * holding a point in time reads like them and honors the table's Date Format setting.
 *
 * @param nanos - The property value in nanoseconds since the epoch
 * @param format - The table's Date Format setting
 * @param timezone - The timezone to display the date in
 * @returns The formatted date
 */
export const formatTimestampPropertyValue = (nanos: number, format: string, timezone: { name: string }): string =>
  formatTableDate(nanosToMillis(nanos), format, timezone);

export const isStartOrEndColumn = (column: ColumnOrRow | undefined) =>
  _.includes([COLUMNS_AND_STATS.startTime.key, COLUMNS_AND_STATS.endTime.key], column?.key);

export const getTextValueForConditionHeader = (
  header: ConditionTableHeader,
  column: ColumnOrRowWithDefinitions | undefined,
) => {
  if (!column) {
    return '';
  }
  if (column.type === TableBuilderColumnType.Text) {
    const { cells } = column;
    if (cells && !Array.isArray(cells)) {
      const value = cells[header.key];
      return typeof value === 'string' ? value : '';
    }
    return '';
  }
  if (column.key === COLUMNS_AND_STATS.name.key) {
    return header.name;
  }
  if (column.key === COLUMNS_AND_STATS.valueUnitOfMeasure.key) {
    return header.units;
  }
  return '';
};

export const getSimpleTableMenuActions = ({
  columnType,
  isPresentationMode,
  isViewOnlyMode,
  hideAutoSize,
  isTransposed,
  isGroupingEnabled,
  isInteractiveContent,
}: {
  columnType: string | undefined;
  isPresentationMode: boolean;
  isViewOnlyMode: boolean;
  hideAutoSize: boolean;
  isTransposed: boolean;
  isGroupingEnabled: boolean;
  isInteractiveContent: boolean;
}) => {
  if (isPresentationMode) {
    const options: TextHeaderMenuAction[] = [];
    return columnType === TableBuilderColumnType.Text
      ? options
      : options.concat(isInteractiveContent ? [TextHeaderMenuAction.Filter, TextHeaderMenuAction.Sort] : []);
  }

  if (isViewOnlyMode) {
    return columnType === TableBuilderColumnType.Text ? [] : [TextHeaderMenuAction.Filter, TextHeaderMenuAction.Sort];
  }

  const actionsToExclude: TextHeaderMenuAction[] = [];
  if (columnType === TableBuilderColumnType.Text) {
    actionsToExclude.push(TextHeaderMenuAction.Filter, TextHeaderMenuAction.Sort);
  }
  if (hideAutoSize) {
    actionsToExclude.push(...AUTO_SIZE_MENU_ACTIONS);
  }
  if (isTransposed) {
    actionsToExclude.push(TextHeaderMenuAction.GroupByColumn);
  }
  if (!isGroupingEnabled || isTransposed) {
    actionsToExclude.push(TextHeaderMenuAction.AggregateByColumn);
  }

  return Object.values(TextHeaderMenuAction).filter((action) => !actionsToExclude.includes(action));
};

export const getExtraHeaderProps = (
  column: (ColumnOrRowWithDefinitions & { headerOverridden?: boolean; property?: string }) | undefined,
  headers: TableBuilderHeaders,
  displayRange: RangeExport,
  timezone: { name: string },
): { isInput: boolean; textValue: string; isStatic?: boolean } => {
  if (column?.headerOverridden) {
    const textValue = formatHeader(
      headers,
      column.property,
      displayRange.start.valueOf(),
      displayRange.end.valueOf(),
      timezone,
    );
    return { textValue, isInput: false, isStatic: true };
  }
  // check for undefined. Empty string is OK - the user removed the title
  const textValue =
    column?.header ??
    (column?.type === TableBuilderColumnType.Property ? column?.key : i18next.t(column?.shortTitle ?? ''));

  return { textValue, isInput: true };
};

/**
 * Takes in cell data and determines what the display value should be that will be shown in the chart. If the cell
 * is a duration, it will be converted to hours and then displayed in the chart. If the cell is not a duration but
 * does have a value, the value will be displayed. Otherwise, a 0 will be displayed.
 * @returns the display value for the cell
 */
export function getCellDisplayValue(cell: SimpleTableCell): string | number {
  if (cell?.value && !_.isUndefined(cell?.units)) {
    return cell.value;
  }
  if (isDurationValid(cell?.value) && cell?.rawValue) {
    const totalHours = (cell?.rawValue as number) / 3600;
    const truncatedValue = (cell.value as string).slice(0, -2);

    if (totalHours < 60 / 3600) {
      // If the duration is less than a minute, display it as its cell value in hour format
      return `0:00:${truncatedValue}`;
    } else if (totalHours < 24) {
      // If the duration is over a minute and less than a day, display it as its cell value
      return truncatedValue;
    } else {
      // If the duration is more than a day, display it as the cell value and the total number of hours
      return `${truncatedValue} (${totalHours.toFixed(0)} ${i18next.t('UNITS.HOURS_plural')})`;
    }
  }

  return '0';
}

/**
 * Takes in cell data and determines what the value of the cell should be that will be used in the chart. If the
 * cell is a number and not a duration, it will return the cell's raw value. If the cell is a duration, it will
 * return the cell's raw value converted to hours. If the cell is not a number, it will return 0.
 * @returns the value for the cell
 */
export function getCellValue(cell: SimpleTableCell): number {
  if (_.isNumber(cell?.rawValue)) {
    if (!_.isUndefined(cell?.units)) {
      return cell.rawValue;
    } else if (isDurationValid(cell?.value)) {
      return (cell.rawValue as number) / 3600;
    }
  }
  return 0;
}

/**
 * Get the grouping level for a column being sorted taking into account other columns that are being grouped.
 */
export function getGroupingLevel(
  columnKey: string,
  level: number,
  groupingAndSortingValues?: GroupingAndSortingValue[],
): number {
  if (!groupingAndSortingValues) {
    return level;
  }

  if (!groupingAndSortingValues.some((column) => column.grouping)) {
    return level;
  }

  const columnBeingSorted = groupingAndSortingValues.find((column) => column.key === columnKey);
  // Should be impossible, but good to check
  if (!columnBeingSorted) {
    return level;
  }

  const sortedSorts: GroupingAndSortingValue[] = _.sortBy(groupingAndSortingValues, (column) => column.sort.level);

  const columnIndex = sortedSorts.indexOf(columnBeingSorted);
  if (columnIndex === -1) {
    return level;
  }

  // All of the grouped columns below the current one get compacted into a single group column, so reduce the order
  // accordingly.
  let adjustedLevel = sortedSorts.slice(1, columnIndex).reduce((acc, grouping) => {
    if (grouping.grouping) {
      return acc - 1;
    } else {
      return acc;
    }
  }, level);
  const firstGrouping = sortedSorts.find((column) => column.grouping)!;
  const firstGroupingIndex = sortedSorts.indexOf(firstGrouping);

  // If we have at least one grouping column, said grouping column is NOT the first column being sorted, and the current
  // column is NOT a grouping column is after said grouping column, we need to bump the sort by 1 to account for
  // adding a dummy grouping column as the first sort.
  if (firstGroupingIndex >= 1 && columnIndex > firstGroupingIndex) {
    adjustedLevel += 1;
  } else if (firstGroupingIndex > columnIndex) {
    // If the first grouping column is being sorted after the current column, we need to bump the sort by 1 to
    // account for the dummy column either taking our place or the place of a column before us.
    adjustedLevel += 1;
  }

  // We consider the group to always be level 1
  return adjustedLevel - 1;
}

export function shouldShowConditionSetupHelp({
  showConditionChartView,
  data,
  rows,
  columnDefs,
  hideHeaderRow,
  isTransposed,
  showCapsuleHeaders,
  statRowsIsSelected,
}: {
  showConditionChartView: boolean;
  data: TableBuilderRowData[];
  rows: ColumnOrRowWithDefinitions[];
  columnDefs: ColDef<TableBuilderRowData>[];
  hideHeaderRow: boolean;
  isTransposed: boolean;
  showCapsuleHeaders: boolean;
  statRowsIsSelected: (data: TableBuilderRowData[]) => boolean;
}) {
  const hasNoRows = _.isEmpty(rows);
  const hasNoSeries = !columnDefs.some((col) => col.chartDataType === 'series');
  const noStatRowsSelected = !statRowsIsSelected(data);

  const chartConditions = showConditionChartView && ((noStatRowsSelected && hasNoRows) || hasNoSeries);

  const tableConditions = hasNoRows && ((hideHeaderRow && !isTransposed) || !showCapsuleHeaders);

  return chartConditions || tableConditions;
}
